import { Injectable } from '@angular/core';
import type { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import type {
  NavigationConfig,
  UrlStateConfig,
  UrlStateItem,
} from '@cdw-ae/url-params';
import { ReportsNavigationConfig } from '../../../../layouts/internal/components/reports/configs/reports-navigation.config';
import {
  DesignationsFilterSettings,
  DeviceSearchFilterSettings,
  DevicesChromeProfileWipeSettings,
  OuCollectionsFilterSettings,
  RolesFilterSettings,
  StateAnalysisReportFilterSettings,
  UserCollectionsFilterSettings,
} from '../configs/filter-bar-config.config';
import { DashboardNavigationConfig } from '../configs/navigation.config';
import {
  DeviceSearchUrlParamConfig,
  GenericUrlParamConfig,
} from '../configs/url-param-adapter.config';
import type {
  AppRoute,
  RouteConfigs,
} from '../models/route-config.model';
import {
  FilterBarId,
  FILTER_QUERY_PARAM_PREFIXES,
} from '../models/route-config.model';

const ROUTE_CONFIG_MAP: Partial<Record<AppRoute, RouteConfigs>> = {
  '/dashboard': {
    urlParamConfigs: {
      dashboard: {
        ...GenericUrlParamConfig,
        queryParamPrefix: FILTER_QUERY_PARAM_PREFIXES[FilterBarId.Dashboard],
      },
    },
    filterBarConfigs: {
      dashboard: StateAnalysisReportFilterSettings,
    },
    navigationConfig: DashboardNavigationConfig,
  },
  '/deviceSearch': {
    urlParamConfigs: {
      deviceSearch: {
        ...DeviceSearchUrlParamConfig,
        queryParamPrefix:
          FILTER_QUERY_PARAM_PREFIXES[FilterBarId.DeviceSearch],
      },
    },
    filterBarConfigs: {
      deviceSearch: DeviceSearchFilterSettings,
    },
  },
  '/userCollections': {
    filterBarConfigs: {
      userFilters: UserCollectionsFilterSettings,
    },
  },
  '/designations': {
    filterBarConfigs: {
      designationFilters: DesignationsFilterSettings,
    },
  },
  '/ouCollections': {
    filterBarConfigs: {
      ouFilters: OuCollectionsFilterSettings,
    },
  },
  '/roles': {
    filterBarConfigs: {
      roleFilters: RolesFilterSettings,
    },
  },
  '/reports': {
    urlParamConfigs: {
      reports: {
        ...GenericUrlParamConfig,
        queryParamPrefix: FILTER_QUERY_PARAM_PREFIXES[FilterBarId.Reports],
      },
    },
    filterBarConfigs: {
      reports: StateAnalysisReportFilterSettings,
    },
    navigationConfig: ReportsNavigationConfig,
  },
  '/jobs/devices/chrome-profile-wipe': {
    urlParamConfigs: {
      chromeProfileWipeFilters: {
        ...GenericUrlParamConfig,
        queryParamPrefix:
          FILTER_QUERY_PARAM_PREFIXES[FilterBarId.ChromeProfileWipeFilters],
      },
    },
    filterBarConfigs: {
      chromeProfileWipeFilters: DevicesChromeProfileWipeSettings,
    },
  },
  '/report-viewer/:reportType': {
    urlParamConfigs: {
      reportFilters: {
        ...GenericUrlParamConfig,
        queryParamPrefix: FILTER_QUERY_PARAM_PREFIXES[FilterBarId.ReportFilters],
      },
    },
    filterBarConfigs: {
      reportFilters: StateAnalysisReportFilterSettings,
    },
  },
} as const;

@Injectable({ providedIn: 'root' })
export class RouteConfigService {
  private routeConfigs = ROUTE_CONFIG_MAP;

  /**
   * Normalize route to base path by removing dynamic segments
   * Example: /report-viewer/battery-health -> /report-viewer/:reportType
   */
  private normalizeRoute(route: string): string {
    // Check if route matches a parameterized route pattern
    // For /report-viewer/:reportType, match /report-viewer/anything
    if (route.startsWith('/report-viewer/')) {
      return '/report-viewer/:reportType';
    }
    return route;
  }

  getRouteConfig(route: string): RouteConfigs | undefined {
    const normalizedRoute = this.normalizeRoute(route);
    return this.routeConfigs[normalizedRoute as AppRoute];
  }

  getFilterBarIds(route: string): string[] {
    const config = this.getRouteConfig(route);
    return Object.keys(config?.filterBarConfigs || {});
  }

  hasMultipleFilterBars(route: string): boolean {
    return this.getFilterBarIds(route).length > 1;
  }

  getNavigationConfig(
    route: string
  ): NavigationConfig<UrlStateItem>[] | undefined {
    return this.getRouteConfig(route)?.navigationConfig;
  }

  getAllUrlParamConfigs(
    route: string
  ): Partial<Record<FilterBarId, UrlStateConfig>> | undefined {
    return this.getRouteConfig(route)?.urlParamConfigs;
  }

  getAllFilterBarConfigs(
    route: string
  ): Partial<Record<string, FilterBarField[]>> | undefined {
    return this.getRouteConfig(route)?.filterBarConfigs;
  }

  getUrlParamConfig(
    route: string,
    filterBarId: FilterBarId
  ): UrlStateConfig | undefined {
    return this.getRouteConfig(route)?.urlParamConfigs?.[filterBarId];
  }

  getFilterBarConfig(
    route: string,
    filterBarId: string
  ): FilterBarField[] | undefined {
    return this.getRouteConfig(route)?.filterBarConfigs?.[
      filterBarId as FilterBarId
    ];
  }
}
